import React from 'react';
import { Search, Bookmark, type LucideIcon } from 'lucide-react';
import Button from '@shared/ui/Button';

export interface FilterActionsProps {
  /** Clears every filter. Omit only if a panel genuinely cannot be cleared. */
  onClear?: () => void;
  /** Saves the current combination. Omit where a panel has no saved filters. */
  onSave?: () => void;
  /** Button text; the saved-views surfaces say "Save View". */
  saveLabel?: string;
  /** Button icon. Default Bookmark; the bills page passes Plus — Bookmark is
      that page's ratified track-a-bill glyph, and the same wizard-opening
      action wears Plus in the views card (one glyph per meaning). */
  saveIcon?: LucideIcon;
  /**
   * Submits the query. Omit where filters apply as they change — a Search
   * button on an auto-applying panel invites a click that does nothing.
   */
  onSearch?: () => void;
  /**
   * True when anything is set. Drives the disabled state of Clear and Save;
   * neither is hidden, so the row never reflows as filters come and go.
   */
  hasFilters?: boolean;
  /** Renders Search as a form submit rather than an onClick handler. */
  searchIsSubmit?: boolean;
  /** Shows the Search button busy (spinner, disabled) while a query is in flight. */
  searching?: boolean;
  className?: string;
}

/**
 * The Clear / Save / Search row, in one place.
 *
 * Every filter panel had built its own, and they disagreed on all three axes a
 * user can actually perceive:
 *
 *   order       Legislation was Clear -> Save; CF was Clear -> Save -> Search
 *   emphasis    Save was `primary` on Legislation and `outline` on CF; Clear
 *               was a secondary Button on Legislation, a bare text link on CF,
 *               and an ✕ inside the input on Budget
 *   wording     "Save this Filter" / "Save Filter" / "Save filter" all shipped,
 *               likewise "Clear Filters" / "Clear all filters" / "Clear filter"
 *
 * The layout here follows the LTR convention shared by Material, Apple's HIG,
 * GitHub Primer and Atlassian: emphasis increases left to right, primary action
 * rightmost. Clear is the lowest-emphasis control in the group — it is mildly
 * destructive and is not what the user came to do — so it is a ghost button,
 * not a solid one. CF was already closest to this; Legislation was furthest.
 *
 * One primary per group, and it is Search: saving a filter is a secondary
 * affordance on a task you are already performing.
 *
 * Disabled rather than hidden. CF hid Clear whenever no filters were set, which
 * reflowed the row as you typed.
 */
const FilterActions: React.FC<FilterActionsProps> = ({
  onClear,
  onSave,
  saveLabel = 'Save filter',
  saveIcon = Bookmark,
  onSearch,
  hasFilters = true,
  searchIsSubmit = false,
  searching = false,
  className = '',
}) => (
  <div className={`flex items-center justify-end gap-2 ${className}`}>
    {onClear && (
      <Button variant="ghost" size="sm" onClick={onClear} disabled={!hasFilters}>
        Clear filters
      </Button>
    )}
    {onSave && (
      <Button variant="outline" size="sm" icon={saveIcon} onClick={onSave} disabled={!hasFilters}>
        {saveLabel}
      </Button>
    )}
    {onSearch && (
      <Button
        type={searchIsSubmit ? 'submit' : 'button'}
        variant="primary"
        size="sm"
        icon={Search}
        loading={searching}
        onClick={searchIsSubmit ? undefined : onSearch}
      >
        Search
      </Button>
    )}
  </div>
);

export default FilterActions;
